import { faceRects } from './partFaces';
import { formatDims } from './formatUnits';
import { namedIn, shownId, withLetter } from './partsCatalogue';
import { isHardwareOrTool, isLeftRight, type PartItem } from './partsRun';
import { subStepKindDef } from './subStepKinds';
import { boresHole, holeSpacingMm, holeWidthOf, makesOwnHole, WALL_MM, type StepHole } from './stepHoles';
import type { ByPart } from './partModelSize';
import type { SubStep } from './stepsRun';
import type { HardwareInteraction } from './hardwareInteractionsRun';
import { GAP } from './stepAnalyzePrompt';
import { patchWords, slotRuns, type HoleSlot } from './holeSlots';
import { subStepLabel } from './stepsRun';

// The FULL Phase 4 hole prompt, authored HERE on the client and sent verbatim — the same
// contract every other prompt in this app has, so the badge beside the button shows the string
// the server receives.
//
// WHY IT IS A SECOND CALL AND NOT A BLOCK ON /step-analyze, which is the obvious place for it:
// the analysis is the call that INVENTS THE PIECE IDS. Re-running it renumbers every piece in
// the step and rewrites every sentence, so a hole answered wrongly could only be fixed by
// re-deriving the whole step and paying for all of it. Asked apart, a wrong hole is one button.
//
// The precedent is beside it: Phase 3 asks two questions about one step, and the transcription
// was split off the analysis on this exact argument (see stepInstructionsRun, which spells it
// out). The analysis prompt has spent its whole history SHEDDING questions — the movement went
// to the assembly phase, the tools and the printed text went to Phase 3 — and a geometry
// question added back to it would run against the grain that pruning was along.
//
// AND IT READS THE ANALYSIS RATHER THAN REDOING IT. The sub-steps are given: their kinds, their
// sentences, which part each gesture adds and which receives it, which of a mirror pair. This
// call is not asked which gesture does what. It is asked, of gestures already broken out, where
// the hardware goes IN.

// WHAT ONE CALL IS TOLD: THE SUB-STEPS OF A STEP THAT PUT THE SAME HARDWARE INTO THE SAME PARTS.
//
// It was one call per STEP, with every gesture of the step in one prompt — twenty of them on a
// chair's first step, most of them tightening a fastener already in or capping one with a wood
// button, plus a table of what every assembled piece was made of. Then one per drilling
// gesture, which was right about the question and twenty calls too many: the four barrel nuts
// going into the two side rails are one question asked four times. So the gestures are grouped
// by the HARDWARE TABLE'S ROW they are (see drillGroupKey) — the barrel nuts with the screws that
// thread into them — and a group is one call. It answers LINES, where each piece of hardware
// enters; the holes along each are worked out from the models (see holeLines). See stepHolesRun,
// which walks the groups in manual order and tells each what the ones before it found.
//
// Built by the caller and exported so the badge shows the exact block sent.
export type HolesContext = {
  step: number;
  // The gestures being asked about — one group, in manual order.
  subs: SubStep[];
  // The whole parts list — the ids everything here is answered in.
  items: PartItem[];
  // Every piece nameable here, with the name its maker gave it.
  pieces: Map<string, { name: string }>;
  // THE COMPONENT PARTS THIS GESTURE CAN BORE INTO — every plank inside the pieces it names,
  // which is the "made of" table reduced to the only rows this question needs.
  drilled: string[];
  // Holes already declared in those parts, earlier in the manual or earlier in this step, so a
  // gesture entering one reuses it instead of declaring a second bore in the same place.
  known: StepHole[];
  // What each part's 3D model measures, by part id.
  models: ByPart;
  // The id this gesture's first new hole takes — numbered by the run, as the lines are turned
  // into holes (see holeLines).
  first: string;
  // THE HARDWARE TABLE'S ROWS these gestures are, whose sentence says how the hardware engages
  // and whose component parts are the ones a line can cross. Empty before that table is run.
  rows: HardwareInteraction[];
  // THE GROUPS OF HOLES PICTURED ("H101, H102"), in the order their pictures follow the step's pages —
  // see jointShots and viewsBlock. Empty until they are drawn.
  pictured: string[];
  // THE HOLES ASKED FOR, numbered, with all that is known of each — see holeSlots. Empty where the
  // hardware table lists none for these rows: then nothing is asked.
  slots: HoleSlot[];
};


// WHICH GESTURES GET A CALL: a component part and a piece of hardware combined (kind 3, or a
// gesture nobody classified), where the hardware BROUGHT is one that goes into wood. A driven or
// adjusted fastener (kind 4) is already in its hole, and a cover or washer bores nothing — see
// boresHole. What is brought is read through the pieces: "101, screw with spring washer" is a
// screw going in.
export function drillsHole(
  sub: SubStep,
  items: PartItem[],
  contents: Map<string, string[]>,
  carried = false,
): boolean {
  if (sub.kind != null && sub.kind !== 3 && !(carried && sub.kind === 1)) return false;
  return broughtOf(sub, carried).some((u) => expand(u.id, contents)
    // A part the list no longer has (re-listed since the steps were read) bores nothing.
    .some((id) => boring(items, id)));
}

const boring = (items: PartItem[], id: string) =>
  !!items[Number(id)] && isHardwareOrTool(items[Number(id)]) && boresHole(items[Number(id)]);

// WHAT A GESTURE BRINGS: its added parts, or all of them where it gives none a role. `carried`
// (Louis 2026-10-29): AND, on a part laid on a part, the hardware already in the one laid — the front
// legs going onto the frame with their dowels in work the dowels' holes, which no other gesture does.
// For the timing only (see relinkHoles); Find holes is asked about the gestures that insert.
const broughtOf = (sub: SubStep, carried: boolean) => {
  const parts = sub.parts ?? [];
  if (!parts.some((u) => u.role)) return parts;
  const added = parts.filter((u) => u.role === 'added');
  return carried && sub.kind === 1 ? [...added, ...parts.filter((u) => u.role === 'prepared')] : added;
};

// A part's id, or the supplied parts inside a piece.
const expand = (id: string, contents: Map<string, string[]>): string[] =>
  contents.get(id) ?? [id];

// WHAT A DRILLING GESTURE IS, FOR GROUPING: the hardware table's row it is. Two gestures of one
// row are the same question and go in one call — the barrel nuts and the screws threading into
// them are one set of lines, and asked apart the nut's hole could not be put where the screw ends.
//
// NULL WHERE NO ROW NAMES ITS HARDWARE, and then it is not asked at all: the table is the
// authority on which hardware goes into what, and a hole looked for with no row behind it was a
// guess from the parts on the page (see unmatchedDrilling, which says which were left out).
export function drillGroupKey(
  sub: SubStep,
  items: PartItem[],
  contents: Map<string, string[]>,
  touch: HardwareTouch,
): string | null {
  const { rows } = drillGroupParts(sub, items, contents, touch);
  return rows.length ? jointKey(rows[0]) : null;
}

// BY WHAT THE ROW JOINS, NOT BY THE ROW: a mirror pair's two entries (see EntrySide) are the
// same hardware in the same parts, one per hand — one joint, asked once, with both hands' holes.
export const jointKey = (row: Pick<HardwareInteraction, 'hardware' | 'components'>) =>
  `row#${row.hardware.join(',')}|${[...row.components].sort().join(',')}`;

// A GROUP'S ROWS, as the joint view reads them: the rows its gestures are (`own`), and every row
// joining what they join in its step — a mirror pair's other hand. In its STEP, by the number in
// front of a row's sub-steps ("2-3"), and not at one of its sub-steps: those letters are the
// analysis the table was read against, which a Phase 4 re-run renumbers (see drillGroupParts).
// One function, so the view's check and the run read the same rows.
export const stepOfLabel = (label: string) => /^\d+/.exec(label.trim())?.[0] ?? '';
export const jointRows = (own: HardwareInteraction[], interactions: HardwareInteraction[], labels: string[]) => {
  const key = own[0] ? jointKey(own[0]) : null;
  const steps = new Set(labels.map(stepOfLabel));
  return interactions.filter((r) => (key != null && jointKey(r) === key
    && r.substeps.some((l) => steps.has(stepOfLabel(l)))) || own.includes(r));
};

// WHAT THE HARDWARE TABLE SAYS HARDWARE TOUCHES: its rows, and the step whose gestures are being
// read — a row lists its sub-steps as "2-12", with the step's number in front.
export type HardwareTouch = { interactions: HardwareInteraction[]; step: number };

// AND THE TWO HALVES OF IT, by part id — what a group is formed by and named by — and the
// hardware table's row it is.
//
// THE ROW: one naming the hardware this gesture brings — on the hand the gesture says, or AT this
// sub-step where the table was read against this analysis, or the only row of this step it can
// be. Its component parts are the ones the hardware goes through, and its boring hardware is
// what the group is named by. Without a row — no table yet — every part the gesture can bore
// into, read through the pieces.
export function drillGroupParts(
  sub: SubStep,
  items: PartItem[],
  contents: Map<string, string[]>,
  touch?: HardwareTouch,
  carried = false,
): {
  drilled: string[]; bores: string[]; row: HardwareInteraction | null;
  // THE ROWS IT IS ONE OF: its row, or — where it says no hand and two rows of one joint fit, a
  // left and a right — both. Which joint it belongs to is then known, and which hand is not.
  rows: HardwareInteraction[];
} {
  const { drilled: can } = gestureParts(sub, items, contents);
  const parts = sub.parts ?? [];
  const bores = [...new Set(broughtOf(sub, carried).flatMap((u) => expand(u.id, contents)))]
    .filter((id) => boring(items, id))
    .sort((a, b) => Number(a) - Number(b));
  if (!touch) return { drilled: can, bores, row: null, rows: [] };
  const label = subStepLabel(touch.step, sub.letter);
  const ofStep = (s: string) => stepOfLabel(s) === String(touch.step);
  const withIt = touch.interactions.filter((row) => row.hardware.some((h) => bores.includes(h)));
  // A MIRROR PAIR'S TWO ENTRIES may both list this sub-step (see EntrySide): the one for the
  // side this gesture holds, where it says; else the first.
  const held = new Set(parts.flatMap((u) => (u.side ? [u.side] : [])));
  // A GESTURE THAT SAYS WHICH HAND IS MATCHED BY WHAT IT IS, NOT BY ITS LABEL: the row of this
  // step with its hardware, that hand, and one of the parts it works on. A row's sub-steps are
  // the labels of the analysis the table was read against, and Phase 4 run again renumbers them:
  // matched by label, the second rail's gestures fell on the first rail's row and the joints
  // lost their right-hand holes. The step's number is the one thing in a label a re-run keeps.
  const ofThis = withIt.filter((r) => r.substeps.some(ofStep));
  const sided = (ofThis.length ? ofThis : withIt).filter((r) => !r.side || held.has(r.side));
  // NO HAND SAID: by its label where the table was read against this very analysis (a stale
  // row's labels are another analysis's, see matchingRows); else the one row it can be, and NO
  // ROW where two can — a left and a right one. It used to take the first, which is how the
  // second rail's gestures came to claim the first rail's holes: a guess, and a silent one.
  // Two rows of ONE joint still say which joint the gesture is in (`rows`), which is all the
  // joint needs: its holes are its rows' (see holesOfJoint), and only their timing is open.
  const fits = ofThis.length ? ofThis : withIt;
  const onIt = fits.filter((r) => r.components.some((id) => can.includes(id)));
  const could = onIt.length ? onIt : fits;
  const atIt = withIt.filter((r) => !r.stale && r.substeps.includes(label));
  const row = held.size
    ? sided.find((r) => r.components.some((id) => can.includes(id))) ?? sided[0] ?? null
    : atIt[0] ?? (could.length === 1 ? could[0] : null);
  const oneJoint = !held.size && could.length > 1 && could.every((r) => jointKey(r) === jointKey(could[0]));
  const rows = row ? [row] : oneJoint ? could : [];
  if (!rows.length) return { drilled: can, bores, row: null, rows };
  const some = rows[0];
  const drilled = some.components.filter((id) => items[Number(id)] && !isHardwareOrTool(items[Number(id)]));
  return {
    drilled: drilled.length ? drilled : can,
    bores: some.hardware.filter((id) => boring(items, id)).sort((a, b) => Number(a) - Number(b)),
    row,
    rows,
  };
}

// THE PLANKS ONE GESTURE CAN BORE INTO, through the pieces it names. In id order, each once.
function gestureParts(
  sub: SubStep,
  items: PartItem[],
  contents: Map<string, string[]>,
): { drilled: string[] } {
  const all = new Set<string>();
  for (const u of sub.parts ?? []) expand(u.id, contents).forEach((id) => all.add(id));
  const ids = [...all].filter((id) => items[Number(id)]).sort((a, b) => Number(a) - Number(b));
  return {
    drilled: ids.filter((id) => !isHardwareOrTool(items[Number(id)])),
  };
}

export const nameOf = (items: PartItem[], id: string) => {
  const p = items[Number(id)];
  return withLetter(namedIn(p, 'en') || `#${id}`, p?.letter);
};

// A COMPONENT IN ONE LINE: its box, and whether it is a mirror pair. Its six faces used to be
// listed with a ruler each — six lines a part saying the same rule six times, which RULES now
// says once.
const componentLine = (items: PartItem[], models: ByPart, id: string): string => {
  const p = items[Number(id)];
  const m = models[id] ?? null;
  const size = m
    ? `${Math.round(m.x)} x ${Math.round(m.y)} x ${Math.round(m.z)} mm (width x depth x height, its 3D model)`
    : faceRects(p.size) ? formatDims(p.size) : 'NOT MEASURED: no size is known, so no place can be given on it';
  const pair = isLeftRight(p) ? ' — a LEFT/RIGHT mirror pair: two objects under one id' : '';
  return `  ${shownId(id)}: ${nameOf(items, id)} — ${size}${pair}`;
};

// A PIECE OF HARDWARE AS WHAT IT IS: its designation and its length and body, not a box — a box
// round a screw came out "14.5 x 0 x 14.5". Only boring hardware is listed (a slot's), so not
// whether it bores.
export const hardwareLine = (items: PartItem[], id: string): string => {
  const p = items[Number(id)];
  const s = p.size;
  const bits = [
    s?.spec,
    s?.lengthMm ? `${s.lengthMm} mm long` : null,
    s?.bodyDiaMm ? `body diameter ${s.bodyDiaMm} mm` : null,
    s?.headDiaMm ? `head diameter ${s.headDiaMm} mm` : null,
  ].filter(Boolean).join(', ');
  return `  ${shownId(id)}: ${nameOf(items, id)}${bits ? ` — ${bits}` : ''}`;
};

// WHAT THE PICTURES AFTER THE PAGES ARE — two per group of holes (see jointShots): the joint
// assembled; and the face they go in by, straight on and close, with a ruler.
const viewsBlock = ({ pictured }: HolesContext): string => (pictured.length
  ? '\nPICTURES after the pages, two per run, in this order: ' + pictured.join('; ') + '. '
    + 'The joint assembled, from three quarters; then the face the run enters by, straight on: the '
    + 'part a faint grey, where the next part touches it outlined in red, and shaded green inside '
    + 'that outline the area where the run\'s first hole must go — from there the hardware\'s whole '
    + 'line stays in wood and reaches far enough into the part it stops in — and rulers in mm.'
  : '');

// THE ID AS IT IS SHOWN, and only where showing it means shifting it.
//
// `shownId` adds one, because a part's id is its position in a list that starts at zero and a
// model handed a list starting at zero answers about the wrong row (see partsCatalogue, where
// that offset is argued). A PIECE's id is not a position in anything — it is 101, 201, minted
// by the analysis — and putting it through the same function renames it: the gesture list came
// out saying 102 for the piece the composition block below calls 101, which is two ids for one
// object in one prompt.
//
// The test is the one this app tells them apart by everywhere: is there a part at that index.
const idShown = (id: string, items: PartItem[]) =>
  (items[Number(id)] ? shownId(id) : id);

// THE STEP'S GESTURES, each with the two things this call reads off it: which of the four kinds
// it is, and which part it brings against which. The sentence is given in English with its
// parts named, because that is the sentence the model would otherwise have to reconstruct from
// the id list under it.
// EXPORTED for the hardware table's call, which reads every step's gestures and so labels each
// with its step as well — "2-3" — where this call, about one step, needs the number alone.
//
// `compact`: ONE LINE, for that call, which lists the whole manual — the kind in one word (its
// prompt defines them), and the parts as ids, brought → receiving: the sentence right before
// them already names each one, and the prompt's lists map every id to its name.
export function subBlock(
  sub: SubStep,
  items: PartItem[],
  pieces: Map<string, { name: string }>,
  label = sub.letter,
  compact = false,
): string {
  const kind = subStepKindDef(sub.kind);
  const named = (id: string) => {
    const p = items[Number(id)];
    if (p) return withLetter(namedIn(p, 'en') || `#${id}`, p.letter);
    return pieces.get(id)?.name || `#${id}`;
  };
  const parts = (sub.parts ?? []).map((u) => {
    const role = u.role === 'added' ? 'brought' : u.role === 'receiving' ? 'receives' : '?';
    const side = u.side ? `, ${u.side}` : '';
    return `${idShown(u.id, items)} (${named(u.id)}${side}) ${role}`;
  }).join('; ');
  const text = (sub.textEn || '').replace(/\{([^{}]+)\}/g, (_w, raw) => named(String(raw).trim()));
  if (compact) {
    const ids = (of: (role: string | undefined) => boolean) => (sub.parts ?? [])
      .filter((u) => of(u.role))
      .map((u) => `${idShown(u.id, items)}${u.side ? ` (${u.side})` : ''}`).join(' + ');
    const brought = ids((r) => r === 'added');
    const into = ids((r) => r === 'receiving');
    // No roles (a sub-step analysed before they were asked): the ids alone.
    const flow = brought || into ? `${brought || '?'} → ${into || '?'}` : ids(() => true) || '(no parts)';
    return `  ${label} — ${kind ? kind.short : 'unclassified'}: "${text}" ${flow}`;
  }
  return `  ${label} — ${kind ? kind.what : 'unclassified'}\n`
    + `     "${text}"\n`
    + `     parts: ${parts || '(none)'}`;
}

// ASKED ONLY WHERE EACH RUN ENTERS. Everything else about it — its holes, parts, faces, hardware,
// diameters and depths — is the Drilling table's (the Joints table's holes, the Insertion table's
// figures) and said as that table says it, one run a line (see holeSlots). The place of its first
// hole is the one thing left; the rest of the run follows from it (see holeLines).
const RULES = 'Place the holes of one joint of a furniture manual, from its drawing: the step\'s pages, '
  + 'then renders of the parts\' 3D models. Each run below is one piece of hardware\'s line through the '
  + 'parts, all known but where it enters: say where on that face its first hole is. The rest of the '
  + 'run follows from it.' + GAP
  + 'Faces and edges are as in the FINISHED product seen from its front: a width is counted from the '
  + 'left or the right, a depth from the front or the back, a height from the top or the bottom.';

const named = (items: PartItem[], id: string, side?: string) =>
  `${shownId(id)} (${nameOf(items, id)}${side && isLeftRight(items[Number(id)]) ? `, ${side}` : ''})`;

// A RUN AS THE DRILLING TABLE WRITES IT: "H101-A → H101-B × H101-X".
const runIds = (sl: HoleSlot) => {
  const next = sl.ids.find((x) => x.part === sl.next && x.via === sl.via)?.id;
  const nut = sl.nut ? sl.ids.find((x) => x.via === sl.nut!.via)?.id : undefined;
  return `${sl.id}${next ? ` → ${next}` : ''}${nut ? ` × ${nut}` : ''}`;
};

// A PIECE OF HARDWARE WITH ITS DRILLING FIGURES — the Insertion table's diameter and inside length.
const figures = ({ items, models }: HolesContext, via: string) => {
  const a = models[via]?.insertion;
  const nums = a ? [a.holeDiameterMm != null ? `diameter ${a.holeDiameterMm} mm` : '',
    a.insideMm != null ? `${a.insideMm} mm inside the parts` : ''].filter(Boolean).join(', ') : '';
  return `${nameOf(items, via)}${nums ? ` (${nums})` : ''}`;
};

// THE RUNS, those that share everything bar their ids on one line.
const slotsBlock = (ctx: HolesContext): string[] => {
  const { slots, items, rows } = ctx;
  return slotRuns(slots).map((run) => {
    const [sl] = run;
    const them = run.length > 1 ? 'them' : 'it';
    const washers = [...new Set(rows.flatMap((r) => Object.entries(r.washers ?? {})
      .filter(([, on]) => on === sl.via).map(([w]) => nameOf(items, w))))];
    const hardware = `${figures(ctx, sl.via)}${washers.map((w) => ` + ${w}`).join('')}`
      + `${sl.nut ? ` × ${figures(ctx, sl.nut.via)}` : ''}`;
    const path = `in by the ${sl.face} face of ${named(items, sl.part, sl.side)}`
      + `${sl.next ? `, on into ${named(items, sl.next, sl.side)}` : ''}`;
    // WHERE IT CAN GO: inside the outline where the next part touches the face, on its green area
    // where that was tried on the 3D models (see holeArea) — or, unmeasured, off the drawing.
    const next = named(items, sl.next ?? '', sl.side);
    const green = !sl.area ? ''
      : sl.area.ok.length
        ? ` Only on its green area: there the hardware runs through wood alone, far enough into ${next}. `
          + `The green already allows for each hole's width and ${WALL_MM} mm of wood round it`
          + `${sl.nut ? `, the ${nameOf(items, sl.nut.via)}'s hole across the line included` : ''}.`
        : ` No point of it lets the hardware reach ${next} through wood alone: place ${them} from the drawing.`;
    const where = sl.patch
      ? `\n     ${next} touches that face at ${patchWords(sl.patch)}, outlined in red.${green} `
        + `Read "${sl.axes.u}" and "${sl.axes.v}" off the outline's two rulers, in mm from their 0.`
      : `\n     Place ${them} from the drawing: "${sl.axes.u}" and "${sl.axes.v}", each {"from": <edge>, "mm": n} `
        + 'or {"centred": true}, from the part\'s own edges.';
    // THE OTHER HAND, NOT ASKED: one file, so its runs are these reflected (see foldMirrors).
    const twins = run.flatMap((s) => (s.mirror ? [s.mirror.id] : []));
    const mirrored = sl.mirror ? `\n     ${twins.join(', ')}: the mirror image, not asked.` : '';
    // TWO PIECES OF HARDWARE ON ONE LINE ARE TWO HOLES: both screws were once given one spot.
    const width = (via: string) => holeWidthOf({ via } as StepHole, items, ctx.models);
    const spacing = run.length > 1 ? holeSpacingMm([width(sl.via), sl.nut ? width(sl.nut.via) : null]) : null;
    const apart = spacing != null
      ? `\n     ${run.map((s) => s.id).join(', ')} are ${run.length} separate holes: at least ${spacing} mm apart, centre to centre.`
      : '';
    // A POINTED SCREW HAS NO HOLE TO SEE: the drawing shows where it goes, and that point is the answer.
    const pointed = makesOwnHole(items[Number(sl.via)])
      ? `\n     ${nameOf(items, sl.via)} is a pointed screw: no part has a hole for it, it makes its own. `
        + 'Give the point where it goes in, as the drawing shows it.'
      : '';
    return `  ${run.map(runIds).join('; ')} — ${hardware}, ${path}.${pointed}${where}${apart}${mirrored}`;
  });
};

// The joint, the runs, and the pictures.
const contextBlock = (ctx: HolesContext): string => {
  const { items, drilled, models } = ctx;
  const engages = ctx.rows.map((row) => row.descriptionEn).filter(Boolean);
  return [
    drilled.length ? `PARTS:\n${drilled.map((id) => componentLine(items, models, id)).join('\n')}` : '',
    engages.length ? `\nHOW IT GOES TOGETHER:\n${engages.map((e) => `  ${e}`).join('\n')}` : '',
    `\nRUNS TO PLACE (ids as the Drilling table gives them; → the hole it runs on into, × the hole across it):\n${slotsBlock(ctx).join('\n')}`,
    '\nWhere no figure is printed, estimate it from the drawing.',
    viewsBlock(ctx),
  ].filter(Boolean).join('\n');
};

// WHICH COMPONENT PARTS THIS GESTURE BORES INTO THAT NOTHING HAS MEASURED, as [id, name]. The
// same test componentLine applies, so the panel and the prompt cannot disagree about which parts
// have a size. It exists because the failure was silent: a part with no measurement produced no
// hole and no line anywhere.
export function unmeasuredIn(ctx: HolesContext): [string, string][] {
  return ctx.drilled
    .filter((id) => !ctx.models[id] && !faceRects(ctx.items[Number(id)].size))
    .map((id) => [id, `${shownId(id)} (${nameOf(ctx.items, id)})`]);
}

const TAIL = GAP + 'Reply with only the JSON:\n'
  + '{"places": [{"id": <the run\'s first id>, <axis>: <mm from the ruler\'s 0>, <axis>: <mm>}]}\n'
  + '(where a hole has no outline, each axis is {"from": <edge>, "mm": <number>} or {"centred": true})';

export function buildStepHolesPrompt(ctx: HolesContext): string {
  return RULES + GAP + contextBlock(ctx) + TAIL;
}
